import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CardFields from 'components/shared/payment/CardFields';
import useSubscription from 'hooks/payments/useSubscription';

const DANGER = '#B21616';

/**
 * Diálogo "Actualizar tarjeta" de la suscripción Wompi. Reutilizado por
 * MySubscriptionCard y por la página "Historial de cobros".
 * `subscription` necesita { id, card_brand, card_last4 }.
 * `onUpdated(subscriptionActualizada)` se llama tras un cambio exitoso.
 */
function UpdateCardDialog({ open, onClose, subscription = null, onUpdated = null }) {
  const { t } = useTranslation(['payments']);
  const { id: userId, full_name: fullName } = useSelector((state) => state.auth.user);
  const { updateCard } = useSubscription(userId);

  const [card, setCard] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleClose = () => {
    if (cardBusy) return;
    setCard(null);
    setCardError(null);
    onClose();
  };

  const handleUpdateCard = async () => {
    if (!card?.isValid) return;

    // Evitar registrar la MISMA tarjeta que ya está vigente.
    const newLast4 = (card.number || '').slice(-4);
    if (subscription?.card_last4 && newLast4 === subscription.card_last4) {
      setCardError(t('subscription.manage.sameCard', {
        defaultValue: 'Esa tarjeta ya está registrada. Ingresa una diferente.',
      }));
      return;
    }

    setCardBusy(true);
    setCardError(null);
    try {
      const cardHolder = (fullName || '').trim().length >= 5 ? fullName.trim() : 'Cliente OTRIX';
      const updated = await updateCard(subscription.id, {
        number: card.number,
        exp_month: card.exp_month,
        exp_year: card.exp_year,
        cvc: card.cvc,
        card_holder: cardHolder,
      });
      setCard(null);
      onUpdated?.(updated);
      onClose();
    } catch (e) {
      setCardError(e?.message || t('subscription.manage.updateError'));
    } finally {
      setCardBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main', color: 'text.primary' } }}>
      <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
        {t('subscription.manage.updateCard')}
        <IconButton onClick={handleClose} disabled={cardBusy} size="small"
          aria-label="cerrar"
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary', '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* Tarjeta actual (referencia) */}
          {subscription?.card_last4 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center"
              sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'surface.overlay', border: '1px solid', borderColor: 'hairline.main' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('subscription.manage.currentCard', { defaultValue: 'Tarjeta actual' })}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CreditCardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {subscription.card_brand} •••• {subscription.card_last4}
                </Typography>
              </Stack>
            </Stack>
          )}

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('subscription.manage.newCardHint', { defaultValue: 'Ingresa los datos de la nueva tarjeta.' })}
          </Typography>

          <CardFields onChange={setCard} disabled={cardBusy} />

          {/* Mensaje de error: banner rojo, texto e ícono blancos (estilo toast) */}
          {cardError && (
            <Stack direction="row" spacing={1.2} alignItems="center"
              sx={{ p: 1.5, borderRadius: 2, backgroundColor: DANGER }}>
              <InfoOutlinedIcon sx={{ fontSize: 20, color: 'text.primary' }} />
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{cardError}</Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="outlined" disabled={cardBusy} onClick={handleClose}
              sx={{ py: 1.3, borderRadius: 2, fontWeight: 700, color: 'text.primary', borderColor: 'hairline.strong', '&:hover': { borderColor: 'hairline.strong', background: 'var(--otx-surface-hover)' } }}>
              {t('subscription.manage.cancelUpdate', { defaultValue: 'Cancelar' })}
            </Button>
            <Button fullWidth disabled={cardBusy || !card?.isValid} onClick={handleUpdateCard}
              sx={{ py: 1.3, borderRadius: 2, fontWeight: 800, background: 'var(--otx-cta-bg)', color: 'var(--otx-cta-text)', '&:hover': { background: 'var(--otx-cta-bg)', filter: 'brightness(0.94)' }, '&.Mui-disabled': { background: 'var(--otx-btn-disabled-bg)', color: 'var(--otx-btn-disabled-text)' } }}>
              {cardBusy ? <CircularProgress size={22} sx={{ color: 'var(--otx-cta-text)' }} /> : t('subscription.manage.updateCard')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

UpdateCardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subscription: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    card_brand: PropTypes.string,
    card_last4: PropTypes.string,
  }),
  onUpdated: PropTypes.func,
};

export default UpdateCardDialog;
